import React from 'react';
import { BrowserRouter, Routes as RouterRoutes, Route } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';

// Auth pages
import UserLogin from './pages/user-login';
import UserRegistration from './pages/user-registration';
import PasswordResetPage from './pages/password-reset-page';
import PasswordResetSuccessPage from './pages/password-reset-success-page';
import { AuthProvider } from './contexts/AuthContext';
import RoleBasedDashboard from './pages/RoleBasedDashboard';
import ProtectedRoute from './components/ProtectedRoute';

// Dashboard pages
import CityOfficialDashboard from './pages/city-official-dashboard';
import CityOfficialTeamsPage from './pages/city-official-teams-page';
import PCFAdminDashboard from './pages/pcf-admin-dashboard';
import TeamWorkspace from './pages/team-workspace';
import PCFAdminMeetingsPortal from './pages/pcf-admin-meetings-portal';
import DocumentManagementPortal from './pages/document-management-portal';

// Feature pages
import BusinessDirectoryPage from './pages/BusinessDirectoryPage';
import BusinessDirectoryPortal from './pages/business-directory-portal';
import EventsPage from './pages/events-page';
import EventDetailsPage from './pages/event-details-page';
import GrantOpportunitiesPortal from './pages/grant-opportunities-portal';
import ProblemStatementBrowse from './pages/problem-statement-browse';
import TeamFormationHub from './pages/team-formation-hub';
import ProblemStatementSubmission from './pages/problem-statement-submission';
import LearningPathwayPortal from './pages/learning-pathway-portal';
import CommunicationHub from './pages/communication-hub';
import BusinessProfileManagement from './pages/business-profile-management';

// Job opportunities and AI chat pages
import JobOpportunities from './pages/job-opportunities';
import PostJob from './pages/post-job';
import AIChatPage from './pages/ai-chat';

// Messaging pages
import DirectMessagingInterface from './pages/direct-messaging-interface';
import TeamGroupChat from './pages/team-group-chat';

// Error pages
import NotFound from './pages/NotFound';

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <ScrollToTop />
        <AuthProvider>
          <RouterRoutes>
            {/* Auth Routes */}
            <Route path="/user-login" element={<UserLogin />} />
            <Route path="/user-registration" element={<UserRegistration />} />
            <Route path="/password-reset-page" element={<PasswordResetPage />} />
            <Route path="/password-reset-success-page" element={<PasswordResetSuccessPage />} />

            {/* Dashboard Routes */}
            <Route path="/" element={<RoleBasedDashboard />} />

            {/* Role-specific Dashboard Routes */}
            <Route
              path="/team-workspace"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <TeamWorkspace />
                </ProtectedRoute>
              }
            />

            <Route
              path="/city-official-dashboard"
              element={
                <ProtectedRoute allowedRoles={['city official', 'city_official']}>
                  <CityOfficialDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/city-official-teams-page"
              element={
                <ProtectedRoute allowedRoles={['city_official']}>
                  <CityOfficialTeamsPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/pcf-admin-dashboard"
              element={
                <ProtectedRoute allowedRoles={['pcf admin', 'pcf_admin']}>
                  <PCFAdminDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/pcf-admin-meetings-portal"
              element={
                <ProtectedRoute allowedRoles={['pcf admin', 'pcf_admin']}>
                  <PCFAdminMeetingsPortal />
                </ProtectedRoute>
              }
            />

            <Route
              path="/document-management-portal"
              element={
                <ProtectedRoute allowedRoles={['pcf admin', 'pcf_admin']}>
                  <DocumentManagementPortal />
                </ProtectedRoute>
              }
            />

            {/* Feature Routes - Public or General Access */}
            <Route path="/business-directory" element={<BusinessDirectoryPage />} />
            <Route path="/business-directory-portal" element={<BusinessDirectoryPortal />} />
            <Route path="/business-profile-management" element={<BusinessProfileManagement />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/event-details/:id" element={<EventDetailsPage />} />
            <Route path="/grant-opportunities" element={<GrantOpportunitiesPortal />} />
            <Route path="/problem-statements" element={<ProblemStatementBrowse />} />
            <Route path="/problems" element={<ProblemStatementBrowse />} />
            <Route path="/team-formation" element={<TeamFormationHub />} />
            <Route path="/submit-problem" element={<ProtectedRoute allowedRoles={['city_official']}><ProblemStatementSubmission /></ProtectedRoute>} />
            <Route path="/learning-pathways" element={<LearningPathwayPortal />} />
            <Route path="/communication-hub" element={<CommunicationHub />} />

            {/* Job Opportunities Routes */}
            <Route path="/job-opportunities" element={<JobOpportunities />} />
            <Route path="/post-job" element={<PostJob />} />

            {/* AI Chat Route */}
            <Route path="/ai-chat" element={<AIChatPage />} />

            {/* Messaging Routes */}
            <Route 
              path="/direct-messaging-interface" 
              element={
                <ProtectedRoute>
                  <DirectMessagingInterface />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/team-group-chat" 
              element={
                <ProtectedRoute allowedRoles={['student', 'city_official', 'pcf_admin']}>
                  <TeamGroupChat />
                </ProtectedRoute>
              } 
            />

            {/* Error Route */}
            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;